// ChapterCognitiveHeatmap — 19BO+ B5.3 · dashboard 章×认知层级矩阵热图.
//
// 数据层: chapterCognitiveMatrix(pkg, progress) (stats.ts) 把 wrongAnswers
// ledger 按 caseId × cognitiveLevel 聚合，区分 unresolved（热值）与 total.
// 本组件纯展示 + 轻量钻取: 点章行展开该章的认知 mini 分布条 + 练习入口链接，
// 不重复 weakest-list 的完整错题列表（那是独立功能），也不发 fetch / 写 store.
//
// 设计语言对齐 dashboard 既有元素:
//   - 复用 .cog-${level} 色类（recall..evaluate + unlabeled · 与 CognitiveBar 同源）
//   - 热格强度 4 档: empty / low / mid / high → 同色不同透明度（CSS .heatmap-heat-*）
//   - 表头 + 章行用 grid 布局
//   - 卡片外壳 .dashboard-analytics-card 由调用方 course-dashboard.tsx 提供
//
// 可访问性: 每格 title attr（hover tooltip · 章·层级：未解决/总数）+ 行按钮
// aria-expanded。热格本身是装饰性（语义在 title），故 div 而非 button.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { TFunction } from '../../i18n/use-t.js';
import type { ChapterCognitiveCell } from '../../lib/stats.js';
import { cogLabel } from './dashboard-cards.js';
import type { CognitiveLevel } from '@examforge/shared/schemas';

const COG_LEVELS: ReadonlyArray<CognitiveLevel> = ['recall', 'understand', 'apply', 'analyze', 'evaluate'];

/** Map unresolved count → 4-step heat intensity class suffix.
 *  纯函数 · 导出便于测试（mirror dashboard-cards.test.ts 的 masteryClass 模式）。 */
export function heatClass(unresolved: number): string {
  if (unresolved <= 0) return 'empty';
  if (unresolved <= 2) return 'low';
  if (unresolved <= 5) return 'mid';
  return 'high';
}

export interface ChapterCognitiveHeatmapProps {
  /** Flattened matrix from chapterCognitiveMatrix(). We re-bucket per chapter
   *  internally; the flat shape keeps the data layer simple + testable. */
  matrix: ChapterCognitiveCell[];
  t: TFunction;
}

interface ChapterRow {
  chapterId: string;
  chapterName: string;
  /** level → cell (5 真 levels only · unlabeled dropped from the visual). */
  byLevel: Map<CognitiveLevel, ChapterCognitiveCell>;
}

/** Bucket the flat matrix into per-chapter rows, dropping the 'unlabeled'
 *  column (the heatmap visualizes the 5 real Bloom levels; unlabeled is kept
 *  in the data layer for debugging/parity but not rendered). */
function bucketByChapter(matrix: ChapterCognitiveCell[]): ChapterRow[] {
  const rows = new Map<string, ChapterRow>();
  for (const cell of matrix) {
    if (cell.level === 'unlabeled') continue;
    let row = rows.get(cell.chapterId);
    if (!row) {
      row = { chapterId: cell.chapterId, chapterName: cell.chapterName, byLevel: new Map() };
      rows.set(cell.chapterId, row);
    }
    row.byLevel.set(cell.level, cell);
  }
  // Preserve pkg.cases order (matrix is already emitted in cases × levels order,
  // so insertion order here == cases order).
  return [...rows.values()];
}

export function ChapterCognitiveHeatmap({ matrix, t }: ChapterCognitiveHeatmapProps): React.ReactNode {
  const [expandedChapter, setExpandedChapter] = useState<string | null>(null);
  const rows = bucketByChapter(matrix);

  // Only chapters with at least one non-zero cell are worth showing. The host
  // (course-dashboard) also gates the whole card on cogMatrix.some(unresolved>0),
  // but per-row filtering here keeps the heatmap scannable when some chapters
  // have zero wrong answers.
  const visibleRows = rows.filter((row) =>
    COG_LEVELS.some((lv) => (row.byLevel.get(lv)?.total ?? 0) > 0),
  );

  if (visibleRows.length === 0) return null;

  return (
    <div className="cog-heatmap" data-testid="chapter-cognitive-heatmap" role="table">
      {/* Header row: empty corner + 5 cognitive level labels */}
      <div className="cog-heatmap-row cog-heatmap-head" role="row">
        <div className="cog-heatmap-corner" role="columnheader" />
        {COG_LEVELS.map((lv) => (
          <div
            key={lv}
            className={`cog-heatmap-col-head cog-${lv}`}
            role="columnheader"
            title={cogLabel(lv, t)}
          >
            <span className={`cog-heatmap-dot cog-${lv}`} aria-hidden="true" />
            <span className="cog-heatmap-col-label">{cogLabel(lv, t)}</span>
          </div>
        ))}
      </div>

      {visibleRows.map((row) => {
        const isOpen = expandedChapter === row.chapterId;
        return (
          <div key={row.chapterId} className="cog-heatmap-chapter" role="rowgroup">
            <button
              type="button"
              className="cog-heatmap-row cog-heatmap-row--chapter"
              role="row"
              aria-expanded={isOpen}
              aria-label={`${row.chapterName} ${t('dashboard.heatmapExpandHint')}`}
              data-testid={`chapter-row-${row.chapterId}`}
              onClick={() => setExpandedChapter(isOpen ? null : row.chapterId)}
            >
              <div className="cog-heatmap-corner cog-heatmap-corner--name" role="rowheader">
                {isOpen ? <ChevronDown size={11} aria-hidden="true" /> : <ChevronRight size={11} aria-hidden="true" />}
                <span className="cog-heatmap-chapter-name">{row.chapterName}</span>
              </div>
              {COG_LEVELS.map((lv) => {
                const cell = row.byLevel.get(lv);
                const unresolved = cell?.unresolved ?? 0;
                const total = cell?.total ?? 0;
                const intensity = heatClass(unresolved);
                return (
                  <div
                    key={lv}
                    className={`cog-heatmap-cell cog-heatmap-heat-${intensity} cog-${lv}`}
                    role="cell"
                    data-testid={`cell-${row.chapterId}-${lv}`}
                    title={t('dashboard.heatmapCellTitle', {
                      chapter: row.chapterName,
                      level: cogLabel(lv, t),
                      unresolved,
                      total,
                    })}
                  >
                    {unresolved > 0 ? unresolved : ''}
                  </div>
                );
              })}
            </button>

            {/* Drill-down: mini per-level distribution bar + practice links.
                Reuses the same cog-* color classes. Does NOT list wrong items
                (that's the weakest-list / wrong-answers view's job). */}
            {isOpen && (
              <div className="cog-heatmap-drilldown" data-testid={`drilldown-${row.chapterId}`}>
                <div className="cog-heatmap-drilldown-bar">
                  {COG_LEVELS.map((lv) => {
                    const cell = row.byLevel.get(lv);
                    const unresolved = cell?.unresolved ?? 0;
                    if (unresolved === 0) return null;
                    return (
                      <div
                        key={lv}
                        className={`cog-heatmap-drilldown-seg cog-${lv}`}
                        title={`${cogLabel(lv, t)}: ${unresolved}`}
                      >
                        {unresolved}
                      </div>
                    );
                  })}
                </div>
                <div className="cog-heatmap-drilldown-links">
                  <Link to={`flashcards?case=${row.chapterId}&all=1`} className="cog-heatmap-drilldown-link">
                    {t('dashboard.heatmapPracticeFlashcard')}
                  </Link>
                  <Link to={`quiz?case=${row.chapterId}`} className="cog-heatmap-drilldown-link">
                    {t('dashboard.heatmapPracticeQuiz')}
                  </Link>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
